import type { VclControlId, VclControlState, VclTypeRole } from './types.js';
import { VCL_CONTROL_IDS } from './types.js';

/**
 * Frozen live-customer baseline (INV-10 / AC-17 / G2).
 * Cite: `customer.extensions.css` `:root` `--vcl-*` defaults (prefer live CSS over TS invent).
 * Body weight 400; gaps sm/md/lg 0.5/0.75/1rem; type roles body|body-sm|label|button|h3|h4.
 *
 * Steps are relative: `0` = zero-delta vs live. Do not normalize live->elsewhere without doc.
 */

export const VCL_BASELINE_STEPS: VclControlState = Object.freeze({
  C1: 0,
  C2: 0,
  C3: 0,
  C4: 0,
  C5: 0,
  C6: 0,
  C7: 0,
  C8: 0,
  C9: 0,
  C10: 0,
}) as VclControlState;

export interface VclTypeRoleBaseline {
  readonly sizeRem: number;
  readonly weight: number;
  /**
   * Exact CSS `line-height` string from `customer.extensions.css` (unitless or rem).
   * C1 steps size only — line stays frozen for VCL-on@baseline ≡ VCL-off.
   */
  readonly lineHeightCss: string;
  readonly trackingEm: number;
}

/**
 * Live type roles — frozen to `customer.extensions.css` `--vcl-type-*` defaults (G2/G3).
 * body/body-sm weight 400; label/button 500; h3/h4 600. No title|caption.
 */
export const VCL_TYPE_BASELINE: Readonly<Record<VclTypeRole, VclTypeRoleBaseline>> = Object.freeze({
  body: Object.freeze({ sizeRem: 1, weight: 400, lineHeightCss: '1.5', trackingEm: 0 }),
  'body-sm': Object.freeze({
    sizeRem: 0.875,
    weight: 400,
    lineHeightCss: '1.25rem',
    trackingEm: 0,
  }),
  label: Object.freeze({
    sizeRem: 0.875,
    weight: 500,
    lineHeightCss: '1.25rem',
    trackingEm: 0,
  }),
  button: Object.freeze({ sizeRem: 1, weight: 500, lineHeightCss: '1.375', trackingEm: 0 }),
  h3: Object.freeze({ sizeRem: 1, weight: 600, lineHeightCss: '1.375', trackingEm: 0 }),
  h4: Object.freeze({
    sizeRem: 0.875,
    weight: 600,
    lineHeightCss: '1.25rem',
    trackingEm: 0,
  }),
});

/** Semantic vertical stacks (rem) - matches common Tailwind gap/py used in shell. */
export const VCL_STACK_BASELINE = Object.freeze({
  xs: 0.25,
  sm: 0.5,
  md: 0.75,
  lg: 1,
});

/** Semantic horizontal gaps (rem) — frozen to CSS `--vcl-gap-inline-{sm,md,lg}` (G2). No xs (CSS omits). */
export const VCL_GAP_BASELINE = Object.freeze({
  sm: 0.5,
  md: 0.75,
  lg: 1,
});

/**
 * Control geometry - customer Button recipe live:
 * md = h-10 (40px), lg = h-12 (48px); pad-x md=1rem.
 * EC-01 clamps enforce ≥44 general / ≥48 CTA after tuning.
 */
export const VCL_CONTROL_GEOMETRY_BASELINE = Object.freeze({
  heightMdPx: 40,
  heightLgPx: 48,
  padXMdRem: 1,
  padXLgRem: 1.25,
  minHitPx: 44,
  minCtaHeightPx: 48,
});

/** Card/surface pad - shared Card `p-4` = 1rem. */
export const VCL_SURFACE_PAD_BASELINE = Object.freeze({
  smRem: 0.75,
  mdRem: 1,
  lgRem: 1.5,
});

/**
 * Control radius - live owner `shared/src/tokens/theme.css`:
 * --radius-sm: 0.375rem (6px). Customer aliases only; no shared write-through.
 */
export const VCL_CONTROL_RADIUS_BASELINE = Object.freeze({
  smRem: 0.375,
  mdRem: 0.5,
  lgRem: 0.75,
});

/**
 * Surface/card radius family (independent of C6).
 * Frozen to `customer.extensions.css` aliases → theme `--radius-*`
 * (md=0.5 / lg=0.75 / xl=1). Live Card binds `--vcl-surface-radius-xl`.
 */
export const VCL_SURFACE_RADIUS_BASELINE = Object.freeze({
  smRem: 0.5,
  mdRem: 0.5,
  lgRem: 0.75,
  /** Live Card `rounded-xl` → `var(--radius-xl)` = 1rem. */
  xlRem: 1,
});

/**
 * Content max widths - `customerTopBarStyles.ts` live owners.
 * Tailwind: max-w-xl=36rem, max-w-2xl=42rem (narrow/QR), max-w-4xl=56rem,
 * max-w-6xl=72rem, auth=31.416rem.
 */
export const VCL_CONTENT_MAX_BASELINE = Object.freeze({
  shellRem: 36,
  standardRem: 56,
  wideRem: 72,
  narrowRem: 42,
  authRem: 31.416,
  /** Field stack is % of auth card - not a content-max. Stored as fraction 0-1. */
  authFieldStackFraction: 0.8625,
  /** Context min widths (rem) - C8 clamp floors to avoid ultra-narrow overflow. */
  minShellRem: 18,
  minStandardRem: 20,
  minWideRem: 24,
  minNarrowRem: 18,
  minAuthRem: 16,
});

/**
 * Page gutter token (rem only). Live default 1rem.
 * Safe-area is NOT packed into this token — `.customer-safe-area-x` does
 * `max(token, env(safe-area-inset-left|right))` separately per side.
 */
export const VCL_PAGE_GUTTER_BASELINE = Object.freeze({
  tokenRem: 1,
});

/**
 * Soft elevation — step 0 aliases theme-aware `--shadow-card` / `--shadow-popover`
 * (light/dark diverge in customer.extensions.css). Tuned steps may strengthen with
 * soft documented literals; never admin `--elevation-*` / shared write-through.
 */
export const VCL_ELEVATION_BASELINE = Object.freeze({
  card: 'var(--shadow-card)',
  popover: 'var(--shadow-popover)',
  /** Soft strengthen (step +1) — still soft; not theme-aware (documented). */
  cardStrong: '0 2px 4px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.05)',
  popoverStrong: '0 8px 28px rgba(0, 0, 0, 0.16)',
  cardMax: '0 3px 6px rgba(0, 0, 0, 0.07), 0 8px 18px rgba(0, 0, 0, 0.07)',
  popoverMax: '0 10px 32px rgba(0, 0, 0, 0.2)',
});

export const VCL_RADIUS_SM_LIVE = '0.375rem';
export const VCL_FONT_WEIGHT_MEDIUM_LIVE = 500;

export function createBaselineState(): VclControlState {
  const next = {} as Record<VclControlId, number>;
  for (const id of VCL_CONTROL_IDS) {
    next[id] = VCL_BASELINE_STEPS[id];
  }
  return next;
}

export function isBaselineState(state: VclControlState): boolean {
  return VCL_CONTROL_IDS.every((id) => state[id] === VCL_BASELINE_STEPS[id]);
}
